import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useProjects } from '@/hooks/useProjects';
import { useProfiles } from '@/hooks/useProfiles';
import { useAuth } from '@/hooks/useAuth';
import { useTasks, Task } from '@/hooks/useTasks';
import { useTenantId } from '@/hooks/useTenantId';
import { Loader2 } from 'lucide-react';

interface TaskFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task?: Task | null;
}

export function TaskFormDialog({ open, onOpenChange, task }: TaskFormDialogProps) {
  const { user } = useAuth();
  const { tenantId } = useTenantId();
  const { projects, isLoading: loadingProjects } = useProjects();
  const { profiles, isLoading: loadingProfiles, formatDisplayName } = useProfiles();
  const { createTask, updateTask } = useTasks();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'not_started' as Task['status'],
    priority: 'medium' as Task['priority'],
    project_id: '',
    due_date: '',
    assigned_to_user_id: '',
    assigned_to_name: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (task) {
      const assignedProfile = profiles.find(p => 
        formatDisplayName(p) === task.assigned_to_name
      );
      
      setFormData({
        title: task.title || '',
        description: task.description || '',
        status: task.status,
        priority: task.priority,
        project_id: task.project_id || 'none',
        due_date: task.due_date || '',
        assigned_to_user_id: assignedProfile?.user_id || 'none',
        assigned_to_name: task.assigned_to_name || '',
      });
    } else {
      const currentUserProfile = profiles.find(p => p.user_id === user?.id);
      const currentUserName = currentUserProfile ? formatDisplayName(currentUserProfile) : '';
      
      setFormData({
        title: '',
        description: '',
        status: 'not_started',
        priority: 'medium',
        project_id: 'none',
        due_date: '',
        assigned_to_user_id: currentUserProfile?.user_id || 'none',
        assigned_to_name: currentUserName,
      });
    }
  }, [task, open, profiles, user?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const selectedProfile = formData.assigned_to_user_id !== 'none'
        ? profiles.find(p => p.user_id === formData.assigned_to_user_id)
        : null;
      
      const assignedName = selectedProfile ? formatDisplayName(selectedProfile) : null;

      const payload = {
        user_id: user?.id,
        title: formData.title,
        description: formData.description,
        status: formData.status,
        priority: formData.priority,
        project_id: formData.project_id === 'none' ? null : formData.project_id || null,
        due_date: formData.due_date || null,
        assigned_to_name: assignedName,
      };

      if (task) {
        await updateTask.mutateAsync({ taskId: task.id, ...payload });
      } else {
        await createTask.mutateAsync(payload);
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving task:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{task ? 'Edit Task' : 'Create New Task'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value as Task['status'] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="stuck">Stuck</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value as Task['priority'] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="project">Project</Label>
              <Select
                value={formData.project_id}
                onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                disabled={loadingProjects}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No project</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="due_date">Due Date</Label>
              <Input
                id="due_date"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="assigned_to">Assigned To</Label>
            <Select
              value={formData.assigned_to_user_id}
              onValueChange={(value) => {
                const selectedProfile = value !== 'none'
                  ? profiles.find(p => p.user_id === value)
                  : null;
                
                setFormData({ 
                  ...formData, 
                  assigned_to_user_id: value,
                  assigned_to_name: selectedProfile ? formatDisplayName(selectedProfile) : '',
                });
              }}
              disabled={loadingProfiles}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {profiles.map((profile) => (
                  <SelectItem key={profile.user_id} value={profile.user_id}>
                    {formatDisplayName(profile)}
                    {profile.user_id === user?.id && ' (You)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {task ? 'Update Task' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
